import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const numberOfCopies = await this.#queryNumberOfCopies();
    if (!this.isConnected) return;

    const speed = this.#calculateSpeed(numberOfCopies);

    this.#setNumberOfCopies(numberOfCopies);
    this.#duplicateContent();

    this.#setSpeed(speed);

    window.addEventListener('resize', this.#handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.#handleResize.cancel();
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        // Wrapping the resolve in a setTimeout here and below splits each marquee reflow into a separate task.
        return setTimeout(() => resolve(1), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;

          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;

          setTimeout(() => {
            resolve(marqueeItemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeItemsWidth));
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const speed = Math.sqrt(numberOfCopies) * speedFactor;

    return speed;
  }

  /**
   * Only a change in the number of copies touches the DOM and the animation. Every other resize leaves
   * the running (or paused) animation alone.
   */
  #handleResize = debounce(async () => {
    const newNumberOfCopies = await this.#queryNumberOfCopies();
    if (!this.isConnected) return;

    // Count the item groups in the content itself: the marqueeItems refs also include the clone's groups.
    if (newNumberOfCopies === this.refs.content.children.length) return;

    const offset = this.#readScrollOffset();

    this.#setNumberOfCopies(newNumberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(this.#calculateSpeed(newNumberOfCopies));
    this.#restoreScrollOffset(offset);
  }, 250);

  /**
   * The distance one loop of the animation scrolls: the content plus the gap before its clone.
   * Both move with the wrapper's transform, so the gap between their positions stays the same.
   */
  #getLoopDistance() {
    const { clonedContent } = this;
    if (!clonedContent) return 0;

    return Math.abs(clonedContent.getBoundingClientRect().left - this.refs.content.getBoundingClientRect().left);
  }

  /**
   * How far into its loop the marquee has scrolled, in pixels, or null when it isn't animating
   * (for example under reduced motion).
   *
   * @returns {number | null}
   */
  #readScrollOffset() {
    const animation = this.refs.wrapper.getAnimations()[0];
    const currentTime = Number(animation?.currentTime);
    const duration = Number(animation?.effect?.getComputedTiming().duration);
    const distance = this.#getLoopDistance();

    if (!Number.isFinite(currentTime) || !duration || !distance) return null;

    return ((currentTime % duration) / duration) * distance;
  }

  /**
   * Moves the animation to the same offset in the new, longer or shorter loop. Every loop is a whole
   * number of identical item groups, so the visitor sees the same items in the same place. Only the
   * current time changes: a paused marquee stays paused and doesn't move.
   *
   * @param {number | null} offset - The offset from `#readScrollOffset`, taken before the change.
   */
  #restoreScrollOffset(offset) {
    if (offset === null) return;

    const distance = this.#getLoopDistance();
    if (!distance) return;

    const progress = (offset % distance) / distance;

    // getAnimations() applies the new speed first, so the duration below is the new one.
    for (const animation of this.refs.wrapper.getAnimations()) {
      const duration = Number(animation.effect?.getComputedTiming().duration);
      if (duration) animation.currentTime = progress * duration;
    }
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * Adds or removes repeated item groups so the content holds exactly `numberOfCopies` of them.
   * Calling it again with the same number changes nothing, so reconnects stay idempotent.
   *
   * @param {number} numberOfCopies
   */
  #setNumberOfCopies(numberOfCopies) {
    const { content } = this.refs;
    const firstItems = content.firstElementChild;
    const target = Math.max(numberOfCopies, 1);

    if (!firstItems) return;

    while (content.children.length < target) {
      content.appendChild(firstItems.cloneNode(true));
    }

    while (content.children.length > target) {
      content.lastElementChild?.remove();
    }
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
